// src/context/GeneratingMessagesContext.tsx
//
// Tracks messages with status="generating" globally, so we can notify
// the user when a background agent task completes — even if they navigated
// away from the chat page.

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "@/routing/accountRouting";
import { notifications } from "@mantine/notifications";
import { useNotificationContext } from "@/context/NotificationContext";
import { apiJson } from "@/api/client";

interface GeneratingEntry {
  messageId: string;
  conversationId: string;
  chatTitle?: string;
}

interface GeneratingMessagesContextType {
  /** Call when a new stream starts and the backend returns the placeholder message ID */
  trackGenerating: (entry: GeneratingEntry) => void;
  /** Call when the stream completes successfully (user saw the result live) */
  untrackGenerating: (messageId: string) => void;
}

const GeneratingMessagesContext = createContext<GeneratingMessagesContextType | undefined>(undefined);

const POLL_INTERVAL = 3000;

export function GeneratingMessagesProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<GeneratingEntry[]>([]);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const navigate = useNavigate();
  const { addNotification } = useNotificationContext();

  const trackGenerating = useCallback((entry: GeneratingEntry) => {
    setEntries((prev) => {
      // Avoid duplicates
      if (prev.some((e) => e.messageId === entry.messageId)) return prev;
      return [...prev, entry];
    });
  }, []);

  const untrackGenerating = useCallback((messageId: string) => {
    setEntries((prev) => prev.filter((e) => e.messageId !== messageId));
  }, []);

  // Poll tracked messages to detect completion
  useEffect(() => {
    if (entries.length === 0) return;

    const interval = setInterval(async () => {
      const current = entriesRef.current;
      if (current.length === 0) return;

      // Group by conversationId to minimize API calls
      const byConversation = new Map<string, GeneratingEntry[]>();
      for (const entry of current) {
        const list = byConversation.get(entry.conversationId) ?? [];
        list.push(entry);
        byConversation.set(entry.conversationId, list);
      }

      const completedIds: string[] = [];

      for (const [conversationId, convEntries] of byConversation) {
        try {
          const params = new URLSearchParams({ limit: "5", offset: "0" });
          const messages = await apiJson<any[]>(
            `/chats/conversations/${conversationId}/messages?${params}`,
          );

          for (const entry of convEntries) {
            const msg = messages.find((m: any) => m.id === entry.messageId);
            if (msg && msg.status !== "generating") {
              completedIds.push(entry.messageId);
              if (msg.status === "completed") {
                const convId = entry.conversationId;
                // Clickable Mantine toast for immediate action
                notifications.show({
                  title: "Your answer is ready",
                  message: "Click to view the response",
                  color: "green",
                  autoClose: 8000,
                  onClick: () => {
                    navigate(`/insights/chat/${convId}`);
                    notifications.cleanQueue();
                  },
                  style: { cursor: "pointer" },
                });
                // Persist to notification panel
                addNotification({
                  title: "Your answer is ready",
                  message: "Click to view the response",
                  type: "success",
                  category: "agent",
                  action_url: `/insights/chat/${convId}`,
                  show_toast: false, // Already showing Mantine toast above
                });
              }
            }
          }
        } catch {
          // Silent — polling failure is non-critical
        }
      }

      if (completedIds.length > 0) {
        setEntries((prev) => prev.filter((e) => !completedIds.includes(e.messageId)));
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [entries.length, navigate, addNotification]);

  return (
    <GeneratingMessagesContext.Provider value={{ trackGenerating, untrackGenerating }}>
      {children}
    </GeneratingMessagesContext.Provider>
  );
}

export function useGeneratingMessages(): GeneratingMessagesContextType {
  const context = useContext(GeneratingMessagesContext);
  if (!context) {
    throw new Error("useGeneratingMessages must be used within GeneratingMessagesProvider");
  }
  return context;
}
